import { useEffect, useState } from 'react';
import withComponentAuth from '../../hoc/withComponentAuth';
import { Box, Typography } from '@mui/material';
import { SmartFilter } from '../../components/common/SmartFilter';
import { headerToolTable } from '../../utils/constants';
import Pagination from '../../components/atoms/Pagination';
import { IManageTool } from '../../utils/entities/interface';
import { debounce } from '../../utils/helper';
import useSnackbar from '../../hooks/useSnackbar';
import AddToolModal from './AddToolModal';
import { ToolList } from '../../components/ui/list/ManageTool';
import { addTool, editTool, GetManageTool } from '../../services/managetool';
import { GetPresignedUrl, UploadImageToS3 } from '../../services/service';

interface FormData {
  id?: string;
  name: string;
  code?: string;
  description: string;
  image: string;
  active?: boolean;
}

const emptyToolForm: FormData = {
  id: '',
  name: '',
  code: '',
  description: '',
  image: '',
  active: true,
};

const ToolTable = () => {
  const { SnackbarComponent, showSnackbar } = useSnackbar();
  const [page, setPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [toolList, setToolList] = useState<IManageTool[]>([]);
  const [totalMaterial, setTotalMaterial] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState<string | undefined>(undefined);
  const [addToolModal, setAddToolModal] = useState(false);
  const [formData, setFormData] = useState<FormData>(emptyToolForm);
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    fetchTools(page, rowsPerPage, searchQuery);
  }, [page, rowsPerPage, searchQuery]);

  // Fetch users from API
  const fetchTools = async (
    page: number,
    limit: number,
    searchQuery?: string,
    sort?: 'ASC' | 'DESC',
  ) => {
    try {
      setIsLoading(true);
      const response = await GetManageTool({
        page,
        limit,
        searchQuery,
        sortOrder: sort,
      });
      setToolList(response.data);
      setTotalMaterial(response.pagination.totalRecords);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChangePage = (event: any, newPage: number) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (event: any) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const refeshList = () => {
    fetchTools(page, rowsPerPage, searchQuery);
  };

  const handleSaveRefresh = () => {
    refeshList();
  };

  // Debounced search handler
  const handleSearchRefresh = debounce((search: string) => {
    setSearchQuery(search);
    setPage(1);
  }, 500);

  const handleFilterRefresh = () => {
    console.log('FILTER HAPPENED');
  };

  //   -----------------Calling API for add material-------------------
  async function handleSubmit() {
    try {
      // upload once and reuse; a plain URL string passes through unchanged
      const imageUrl = await resolveImageUrl(formData.image);

      if (formData.id) {
        await editTool({
          name: formData.name,
          code: formData.code,
          description: formData.description,
          image: imageUrl,
          active: formData.active,
          toolId: formData.id,
        }).then((response: any) => {
          if (response.status) {
            showSnackbar('Tool edited successfully', 'success');
            setAddToolModal(false);
            fetchTools(1, 10, '');
            setFormData(emptyToolForm);
          }
        });
      } else {
        await addTool({
          name: formData.name,
          code: formData.code,
          description: formData.description,
          image: imageUrl,
          active: formData.active,
        }).then((response) => {
          if (response.status == 201) {
            showSnackbar('Tool created successfully', 'success');
            setAddToolModal(false);
            fetchTools(1, 10, '');
            setFormData(emptyToolForm);
          }
        });
      }
    } catch (error) {
      console.error('Error saving tool:', error);
      showSnackbar('Failed to save tool. Image upload failed.', 'error');
    }
  }

  // Shared presigned-URL flow (auth header + base URL from the common client);
  // folder type 10 -> tools/image on the server.
  const resolveImageUrl = async (file: any) => {
    if (file && file.name) {
      const presignedUrl = await GetPresignedUrl(file.name, file.type, 10);
      return UploadImageToS3(file, presignedUrl);
    }
    return file;
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxHeight: '100%',
      }}
    >
      {SnackbarComponent}
      <AddToolModal
        open={addToolModal}
        onClose={() => {
          setAddToolModal(false);
          setFormData(emptyToolForm);
          setPreview('');
        }}
        onSaved={() => {
          handleSubmit();
        }}
        setFormData={setFormData}
        formData={formData}
        preview={preview}
        setPreview={setPreview}
      />
      <SmartFilter
        title="Tools"
        createFilter={['SEARCH', 'ADD']}
        filterHeader={'Filter Tool'}
        onNavigate={() => {
          setAddToolModal(true);
        }}
        onSaved={handleSaveRefresh}
        addDrawerSize="lg"
        onFiltered={handleFilterRefresh}
        onSearch={handleSearchRefresh}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          minHeight: 0,
          p: 1,
        }}
      >
        <Box
          sx={{
            height: '100%',
            maxHeight: '100%',
            overflowY: 'scroll',
            scrollbarWidth: 'none',
          }}
        >
          {toolList.length <= 0 && !isLoading ? (
            <Typography textAlign={'center'} marginTop={'20%'}>
              No Material Found
            </Typography>
          ) : (
            <ToolList
              headerCells={headerToolTable}
              toolList={toolList}
              isLoading={isLoading}
              setAddToolModal={setAddToolModal}
              setFormData={setFormData}
              onUpdate={refeshList}
            />
          )}
        </Box>
        <Box display={'block'} bottom={0} right={0}>
          <Pagination
            count={totalMaterial}
            page={page - 1}
            rowsPerPage={rowsPerPage}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        </Box>
      </Box>
    </Box>
  );
};

export default withComponentAuth(ToolTable);
